import React from 'react';
import { cssMap } from '@compiled/react';

import type { AiSuggestionsRightRailEntryPoint } from '@atlaskit/editor-common/analytics';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';

import { SuggestionsPanel } from './SuggestionsPanel.tsx';
import { SuggestionsFooter } from './SuggestionsFooter.tsx';

const styles = cssMap({
	layout: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		overflow: 'hidden',
		// Positioning context for the footer overlay below.
		position: 'relative',
	},
	panelSystem: {
		// PanelBody provides a relative positioning context instead of a fixed height.
		position: 'absolute',
		inset: 0,
	},
	content: {
		flex: '1 1 0%',
		minHeight: '0px',
		overflow: 'auto',
		/**
		 * The footer floats over this area rather than taking a row of its own, so the space
		 * it needs is reserved here instead — visible only once the list is scrolled to the
		 * bottom. `space.300` is the height the footer hugs to (a `space.300` policy row with
		 * `space.050` padding above and below), plus `space.200` of breathing room; revisit
		 * if the footer's content changes.
		 *
		 * `paddingBlockEnd` lets the last card scroll clear of the footer; the matching
		 * `scrollPaddingBlockEnd` keeps anything brought in by `scrollIntoView` (a selected
		 * card, the inline loading bar) above the scrim rather than under it.
		 */
		paddingBlockEnd: `calc(${token('space.300')} + ${token('space.200')})`,
		scrollPaddingBlockEnd: `calc(${token('space.300')} + ${token('space.200')})`,
	},
	/**
	 * The footer floats over the scroll area rather than taking a row of its own, so the
	 * list runs the full height of the panel and scrolls under the scrim.
	 */
	footerOverlay: {
		position: 'absolute',
		insetBlockEnd: 0,
		insetInlineStart: 0,
		insetInlineEnd: 0,
	},
});

export const SuggestionsPanelLayout = ({
	entryPoint,
}: {
	entryPoint?: AiSuggestionsRightRailEntryPoint;
}): React.JSX.Element => (
	<div
		css={[styles.layout, fg('ads-panel-system-confluence-object-sidebar') && styles.panelSystem]}
	>
		<div css={styles.content}>
			<SuggestionsPanel entryPoint={entryPoint} />
		</div>
		<div css={styles.footerOverlay}>
			<SuggestionsFooter />
		</div>
	</div>
);
